import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Button, Dialog, cn, hitTarget44 } from '@natter-co/design-system';
import { monitoring } from 'common/services';

import { FormHeader } from '../FormHeader';
import { OpenTextQuestion } from '../OpenTextQuestion';
import { PulseGlow } from '../PulseGlow';
import { QuestionReadOut } from '../QuestionReadOut';
import { RiseIn } from './FormDialog.styled';

import type { Props } from './types';
import type { DictationStatus } from 'modules/forms/hooks';
import type { FormAnswer, FormAnswers } from 'modules/forms/types';

const DICTATION_ACTIONS: Partial<Record<DictationStatus, `forms.${string}`>> = {
  listening: 'forms.dictation_started',
  blocked: 'forms.dictation_blocked',
  unsupported: 'forms.dictation_unsupported',
};

const PROMPT = 'text-pretty text-heading-lg text-[var(--foreground-primary)]';
// When the focused button unmounts (Share feedback becoming the thank-you), the Base UI focus trap parks focus on
// the popup first, so an immediate focus() is overwritten. Focus once the dialog has settled.
const FOCUS_SETTLE_MS = 50;

const hasText = (answer: FormAnswer | undefined) => !!answer?.value.trim();
// A question not on screen stays in the layout, so it sizes the box, but out of reach: `invisible` (visibility:
// hidden) takes it out of the tab order and pointer reach, and aria-hidden out of the accessibility tree. No `inert`,
// which React 18 and 19 spell differently.
const hiddenIf = (hidden: boolean) => (hidden ? { 'aria-hidden': true } : {});
const noop = () => undefined;

/**
 * The feedback dialog from the post-event-feedback design ("Multi-question: open"): the header lockup, a grey
 * question box that glows while the person types or speaks, Natalie reading each question out, and one footer action
 * (Skip until the question has an answer, then Next, then Share feedback). Full screen on a phone while answering;
 * the thank-you is a floating modal.
 *
 * Ported from `Flow` in the post-event-feedback prototype.
 */
export const FormDialog = ({ form, onSubmit, onClose }: Props) => {
  const { t } = useTranslation('forms', { keyPrefix: 'dialog' });
  const { t: tText } = useTranslation('forms', { keyPrefix: 'openText' });
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<FormAnswers>({});
  const [recording, setRecording] = useState(false);
  const [typing, setTyping] = useState(false);
  const [unheard, setUnheard] = useState(false);
  const [phase, setPhase] = useState<'answering' | 'submitting' | 'error' | 'thanks'>('answering');
  // False until the person moves to another question, or the first question has read out while focus is still on
  // the title (where the dialog opens). After that, focus follows the person to each question's answer box.
  const [focusInput, setFocusInput] = useState(false);
  // Questions already shown: a revisit shows the question whole instead of reading it out again.
  const [seen, setSeen] = useState<ReadonlySet<number>>(() => new Set());
  const headingRef = useRef<HTMLHeadingElement>(null);
  const thanksCloseRef = useRef<HTMLButtonElement>(null);
  const baseId = useId();
  const unheardId = `${baseId}-unheard`;
  const promptId = (index: number) => `${baseId}-prompt-${index}`;

  const question = form.questions[step];
  const total = form.questions.length;
  const isLast = step === total - 1;
  const answered = form.questions.filter((q) => hasText(answers[q.key]));
  const anyAnswered = answered.length > 0;
  const done = phase === 'thanks';
  const submitting = phase === 'submitting';
  const busy = recording || submitting;
  const canSubmit = !busy && (!form.requireAnyAnswer || anyAnswered);
  // Once a share is under way from the last question (Skip can start one), the action is Share feedback: busy while
  // it sends, and the retry if it fails.
  const sharing = isLast && (submitting || phase === 'error');

  const setAnswer = useCallback(
    (answer: FormAnswer) => {
      setAnswers((prev) => ({ ...prev, [question.key]: answer }));
      // An edit is a fresh attempt, so the last failure's message goes.
      setPhase((prev) => (prev === 'error' ? 'answering' : prev));
    },
    [question.key]
  );

  const goTo = (next: number) => {
    setSeen((prev) => new Set(prev).add(step));
    setTyping(false);
    setUnheard(false);
    setFocusInput(true);
    setStep(next);
  };

  // The first question reads out while focus is on the title; then the person can answer straight away.
  const handleReadDone = useCallback(() => {
    const active = document.activeElement;
    if (active === headingRef.current || active === document.body) setFocusInput(true);
  }, []);

  // Mic support and use, per form: this is the evidence for the browser-dictation decision (spec D7).
  // `unsupported` is reported once per dialog, not once per step.
  const reportedUnsupported = useRef(false);
  const handleDictationStatus = useCallback(
    (status: DictationStatus) => {
      const action = DICTATION_ACTIONS[status];
      if (!action) return;
      if (status === 'unsupported') {
        if (reportedUnsupported.current) return;
        reportedUnsupported.current = true;
      }
      monitoring.trackAction(action, { formKey: form.key });
    },
    [form.key]
  );

  const submit = async () => {
    setPhase('submitting');
    // Empty and whitespace-only answers count as unanswered and are left out of the body: a recording
    // discarded with no prior text leaves `{ value: '' }` behind.
    const payload = Object.fromEntries(
      answered.map((q) => [q.key, { ...answers[q.key], value: answers[q.key].value.trim() }])
    );
    setPhase((await onSubmit(payload)) === 'ok' ? 'thanks' : 'error');
  };

  // Skip leaves the question unanswered. On the last question it shares what was given earlier, or closes when
  // there is nothing the form can take.
  const skip = () => {
    monitoring.trackAction('forms.step_skipped', { formKey: form.key, step });
    if (!isLast) goTo(step + 1);
    else if (canSubmit) void submit();
    else onClose('dismissed', step);
  };

  // Cmd or Ctrl + Enter in the answer box (only sent with an answer and nothing recording).
  const handleSubmitKey = () => {
    if (!isLast) goTo(step + 1);
    else if (canSubmit) void submit();
  };

  const requestClose = () => {
    // Every dismissal route is off while submitting; this is the backstop, so a dismiss can't race the thank-you.
    if (submitting) return;
    onClose(done ? 'submitted' : 'dismissed', step);
  };

  useEffect(() => {
    if (!done) return undefined;
    const timer = window.setTimeout(() => thanksCloseRef.current?.focus(), FOCUS_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [done]);

  const hit = hitTarget44.from40;
  // One action: Skip until this question has an answer, then Next, or Share feedback on the last question. While
  // recording it shows Next or Share feedback, held until the recording ends.
  const action =
    !hasText(answers[question.key]) && !recording && !sharing ? (
      <Button appearance="secondary" size="regular" className={hit} onClick={skip}>
        {t('skip')}
      </Button>
    ) : !isLast ? (
      <Button appearance="primary" size="regular" className={hit} disabled={recording} onClick={() => goTo(step + 1)}>
        {t('next')}
      </Button>
    ) : (
      <Button
        appearance="primary"
        size="regular"
        className={hit}
        disabled={!canSubmit}
        loading={submitting}
        onClick={() => void submit()}
      >
        {t('submit')}
      </Button>
    );

  return (
    <Dialog
      open
      // Stable for E2E: the title and questions come from the form service and change without a frontend deploy.
      data-testid={`FormDialog-${form.key}`}
      size="md"
      // Full screen on a phone while answering; the thank-you is a standard floating modal.
      fullScreenOnMobile={!done}
      hideHeader
      // FormHeader draws the visible X, so the Dialog adds no hidden second one.
      showCloseButton={false}
      bodyPadding="none"
      className={cn(!done && 'max-sm:[&>footer]:mt-5')}
      initialFocus={headingRef}
      // A stray click outside must not throw away a spoken answer. The header X is the visible way out,
      // which a touch user on the full-screen mobile dialog has no Escape key for. Neither works mid-submit.
      closeOnBackdropClick={false}
      closeOnEscape={!submitting}
      onOpenChange={(open) => !open && requestClose()}
      title={done ? t('thanksTitle') : form.title}
      // FormHeader draws both visibly; the Dialog keeps them as the accessible name and description.
      description={done ? t('thanksSubtitle') : form.subtitle}
      footer={
        done ? (
          <Button
            ref={thanksCloseRef}
            appearance="primary"
            size="regular"
            className={hit}
            onClick={() => onClose('submitted', step)}
          >
            {t('close')}
          </Button>
        ) : (
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <span>
              {step > 0 && (
                <Button
                  appearance="tertiary"
                  size="regular"
                  className={hit}
                  disabled={busy}
                  onClick={() => goTo(step - 1)}
                >
                  {t('back')}
                </Button>
              )}
            </span>
            <span className="flex items-center gap-4">
              {total > 1 && (
                <span className="text-label-md text-[var(--foreground-secondary)]">
                  {t('stepOf', { n: step + 1, total })}
                </span>
              )}
              {action}
            </span>
          </div>
        )
      }
    >
      {/* On a phone the body fills the screen: the question box grows to meet the footer, 20px above the buttons as
          the buttons are 20px above the bottom of the screen. The thank-you gets 20px below its text. */}
      <div
        className={cn(
          'flex flex-col gap-7 px-5 pt-5 pb-1 max-sm:flex-1 max-sm:gap-5!',
          done ? 'max-sm:pb-5!' : 'max-sm:pb-0!'
        )}
      >
        <FormHeader
          title={done ? t('thanksTitle') : form.title}
          subtitle={done ? t('thanksSubtitle') : form.subtitle}
          done={done}
          onClose={requestClose}
          // The thank-you's footer button is "Close", so its X takes another name.
          closeLabel={done ? t('dismiss') : undefined}
          closeDisabled={submitting}
          headingRef={headingRef}
        />
        {!done && (
          <PulseGlow
            on={recording || typing}
            className="rounded-md bg-[var(--background-page)] p-5 max-sm:flex max-sm:flex-1 max-sm:flex-col"
            contentClassName="grid max-sm:flex-1"
          >
            {/* Every question shares one grid cell, so the box (and the dialog) keeps one size between them. */}
            {form.questions.map((q, index) => {
              const here = index === step;
              return (
                <div
                  key={q.key}
                  className={cn('col-start-1 row-start-1 flex flex-col', !here && 'invisible')}
                  {...hiddenIf(!here)}
                >
                  {/* The question sits centred between the top of the box and the answer box, at least 91px from
                      each, so it sits the same on every step. On a phone the box already grows to fill the screen, so
                      the floor is smaller: on a short phone (375 x 667) the design's floor pushed a few lines of answer
                      below the fold. */}
                  <div className="flex flex-1 flex-col justify-center gap-5 pt-[71px] pb-[91px] max-sm:pt-8 max-sm:pb-10">
                    {here ? (
                      <QuestionReadOut
                        id={promptId(index)}
                        text={q.prompt}
                        instant={seen.has(index)}
                        onDone={handleReadDone}
                        className={PROMPT}
                      />
                    ) : (
                      <p id={promptId(index)} className={cn('m-0', PROMPT)}>
                        {q.prompt}
                      </p>
                    )}
                    {here && unheard && (
                      <RiseIn
                        id={unheardId}
                        className="m-0 text-body-lg text-pretty text-[var(--foreground-secondary)]"
                      >
                        {tText('unheard')}
                      </RiseIn>
                    )}
                  </div>
                  {/* Remounted on arrival, so each visit starts with fresh dictation, typing and unheard state. */}
                  <OpenTextQuestion
                    key={here ? 'current' : 'idle'}
                    question={q}
                    answer={answers[q.key]}
                    onChange={here ? setAnswer : noop}
                    onRecordingChange={here ? setRecording : noop}
                    onDictationStatusChange={here ? handleDictationStatus : undefined}
                    onTypingChange={here ? setTyping : undefined}
                    onSubmit={here ? handleSubmitKey : undefined}
                    onUnheardChange={here ? setUnheard : undefined}
                    unheardId={unheardId}
                    describedBy={promptId(index)}
                    autoFocus={here && focusInput}
                    submitting={submitting}
                  />
                </div>
              );
            })}
          </PulseGlow>
        )}
        {phase === 'error' && (
          <Alert severity="error" placement="inline">
            {t('submitError')}
          </Alert>
        )}
      </div>
      <p className="sr-only" aria-live="polite">
        {done ? t('submitted') : ''}
      </p>
    </Dialog>
  );
};
